import React, { useCallback, useEffect, useMemo } from "react";
import useActiveProfileOrgProOrElite from "../common/hooks/useActiveProfileOrgProOrElite";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

/**
 * Reusable right-side drawer for composing a notification
 */
export default function NotificationComposeDrawer({
                                                      open,
                                                      onClose,

                                                      title,
                                                      setTitle,
                                                      message,
                                                      setMessage,

                                                      segOrg,
                                                      setSegOrg,
                                                      segDivision,
                                                      setSegDivision,
                                                      segSeason,
                                                      setSegSeason,
                                                      segTeams,
                                                      setSegTeams,

                                                      segOrgLabel,
                                                      segDivisionLabel,
                                                      segSeasonLabel,
                                                      segTeamsLabel,

                                                      orgOptions = [],
                                                      divisionOptions = [],
                                                      seasonOptions = [],
                                                      teamOptions = [],
                                                      audience,
                                                      setAudience,
                                                      audienceLabel,
                                                      sending = false,

                                                      onCancel,
                                                      onSend,
                                                  }) {
    const colors = useThemeColors();
    const { t } = useTranslation();
    const { hasAnyOrgElite, hasAnyOrgEnterprise } = useActiveProfileOrgProOrElite();
    const eliteNotifications = hasAnyOrgElite || hasAnyOrgEnterprise;

    const safeTeams = useMemo(
        () => (Array.isArray(segTeams) ? segTeams : []),
        [segTeams]
    );

    const disableTeams = audience === "referees";

    const onToggleTeam = useCallback(
        (teamId) => {
            const value = String(teamId);
            setSegTeams?.((prev) => {
                const arr = Array.isArray(prev) ? prev : [];
                return arr.includes(value)
                    ? arr.filter((x) => x !== value)
                    : [...arr, value];
            });
        },
        [setSegTeams]
    );

    const teamsLabelFallback = useMemo(() => {
        if (!safeTeams.length) return t("notificationComposeDrawer.labels.all");
        return t("notificationComposeDrawer.labels.selectedCount", {
            count: safeTeams.length,
        });
    }, [safeTeams, t]);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    if (!open) return null;

    return (
        <>
            <div
                className="kbola-compose-overlay"
                onClick={onClose}
                role="presentation"
            />

            <div className="kbola-compose-drawer" role="dialog" aria-modal="true">
                <div className="kbola-compose-head">
                    <div className="kbola-compose-head-left">
                        <span className="kbola-compose-badge">
                            <i className="fas fa-bullhorn" />
                        </span>

                        <div className="kbola-compose-head-text">
                            <div className="kbola-compose-title">
                                {t("notificationComposeDrawer.header.title")}
                            </div>
                            <div className="kbola-compose-subtitle">
                                {t("notificationComposeDrawer.header.subtitle")}
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="btn btn-light kbola-compose-close"
                        onClick={onClose}
                        title={t("notificationComposeDrawer.header.close")}
                        disabled={sending}
                    >
                        <i className="fas fa-times" />
                    </button>
                </div>

                <div
                    className="kbola-compose-recipientbar"
                    title={t("notificationComposeDrawer.recipientBar.previewTitle")}
                >
                    <div className="kbola-compose-recipientpill">
                        <i className="fas fa-building mr-2" />
                        <span className="kbola-compose-recipientlabel">
                            {t("notificationComposeDrawer.recipientBar.org")}
                        </span>{" "}
                        <span className="kbola-compose-recipientvalue">
                            {segOrgLabel ?? segOrg}
                        </span>
                    </div>

                    <div className="kbola-compose-recipientpill">
                        <i className="fas fa-sitemap mr-2" />
                        <span className="kbola-compose-recipientlabel">
                            {t("notificationComposeDrawer.recipientBar.division")}
                        </span>{" "}
                        <span className="kbola-compose-recipientvalue">
                            {segDivisionLabel ?? segDivision}
                        </span>
                    </div>

                    <div className="kbola-compose-recipientpill">
                        <i className="fas fa-calendar-alt mr-2" />
                        <span className="kbola-compose-recipientlabel">
                            {t("notificationComposeDrawer.recipientBar.season")}
                        </span>{" "}
                        <span className="kbola-compose-recipientvalue">
                            {segSeasonLabel ?? segSeason}
                        </span>
                    </div>

                    {eliteNotifications && (
                        <div className="kbola-compose-recipientpill">
                            <i className="fas fa-users mr-2" />
                            <span className="kbola-compose-recipientlabel">
                                {t("notificationComposeDrawer.recipientBar.teams")}
                            </span>{" "}
                            <span className="kbola-compose-recipientvalue">
                                {segTeamsLabel ?? teamsLabelFallback}
                            </span>
                        </div>
                    )}

                    <div className="kbola-compose-recipientpill">
                        <i className="fas fa-bullseye mr-2" />
                        <span className="kbola-compose-recipientlabel">
                            {t("notificationComposeDrawer.recipientBar.recipients")}
                        </span>{" "}
                        <span className="kbola-compose-recipientvalue">
                            {audienceLabel ?? audience}
                        </span>
                    </div>
                </div>

                <div className="kbola-compose-body">
                    <div className="kbola-compose-field">
                        <div className="kbola-compose-label">
                            {t("notificationComposeDrawer.fields.title")}
                        </div>
                        <input
                            className="form-control kbola-compose-input"
                            value={title}
                            onChange={(e) => setTitle?.(e.target.value)}
                            placeholder={t("notificationComposeDrawer.fields.titlePlaceholder")}
                            disabled={sending}
                        />
                    </div>

                    <div className="kbola-compose-field">
                        <div className="kbola-compose-label">
                            {t("notificationComposeDrawer.fields.message")}
                        </div>
                        <textarea
                            className="form-control kbola-compose-textarea"
                            value={message}
                            onChange={(e) => setMessage?.(e.target.value)}
                            placeholder={t("notificationComposeDrawer.fields.messagePlaceholder")}
                            rows={6}
                            disabled={sending}
                        />
                    </div>

                    <div className="kbola-compose-divider" />

                    <div className="kbola-compose-field">
                        <div className="kbola-compose-label">
                            {t("notificationComposeDrawer.fields.recipients")}
                        </div>
                        <select
                            className="form-control kbola-compose-select"
                            value={audience || "all"}
                            onChange={(e) => setAudience?.(e.target.value)}
                            disabled={sending}
                        >
                            {eliteNotifications && (
                                <option value="all">
                                    {t("notificationComposeDrawer.recipients.everyone")}
                                </option>
                            )}
                            <option value="managers">
                                {t("notificationComposeDrawer.recipients.managersOnly")}
                            </option>
                            <option value="referees">
                                {t("notificationComposeDrawer.recipients.refereesOnly")}
                            </option>
                        </select>

                        <div className="kbola-compose-hint">
                            {t("notificationComposeDrawer.hints.recipients")}
                        </div>
                    </div>

                    <div className="kbola-compose-field">
                        <div className="kbola-compose-label">
                            {t("notificationComposeDrawer.fields.organization")}
                        </div>
                        <select
                            className="form-control kbola-compose-select"
                            value={segOrg}
                            onChange={(e) => setSegOrg?.(e.target.value)}
                            disabled={sending}
                        >
                            {orgOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="kbola-compose-field">
                        <div className="kbola-compose-label">
                            {t("notificationComposeDrawer.fields.division")}
                        </div>
                        <select
                            className="form-control kbola-compose-select"
                            value={segDivision}
                            onChange={(e) => setSegDivision?.(e.target.value)}
                            disabled={sending}
                        >
                            {divisionOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {segDivision !== "all" && (
                        <div className="kbola-compose-field">
                            <div className="kbola-compose-label">
                                {t("notificationComposeDrawer.fields.season")}
                            </div>
                            <select
                                className="form-control kbola-compose-select"
                                value={segSeason}
                                onChange={(e) => setSegSeason?.(e.target.value)}
                                disabled={sending}
                            >
                                {seasonOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {eliteNotifications && !disableTeams && segSeason !== "all" ? (
                        <div className="kbola-compose-field">
                            <div className="kbola-compose-label">
                                {t("notificationComposeDrawer.fields.teamsMulti")}
                            </div>

                            <div className="kbola-compose-teamchips">
                                {teamOptions.map((team) => {
                                    const active = safeTeams.includes(String(team.value));
                                    return (
                                        <button
                                            key={team.value}
                                            type="button"
                                            className={`kbola-compose-chip ${active ? "is-active" : ""}`}
                                            onClick={() => onToggleTeam(team.value)}
                                            aria-pressed={active}
                                            title={team.label}
                                            disabled={sending}
                                        >
                                            {team.label}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="kbola-compose-hint">
                                {t("notificationComposeDrawer.hints.teams")}
                            </div>
                        </div>
                    ) : null}

                    {eliteNotifications && disableTeams ? (
                        <div className="kbola-compose-hint">
                            {t("notificationComposeDrawer.hints.teamsDisabled")}
                        </div>
                    ) : null}
                </div>

                <div className="kbola-compose-footer">
                    <button
                        type="button"
                        className="btn btn-light kbola-compose-btn"
                        onClick={onCancel}
                        title={t("notificationComposeDrawer.actions.cancel")}
                        disabled={sending}
                    >
                        {t("notificationComposeDrawer.actions.cancel")}
                    </button>

                    <button
                        type="button"
                        className="btn btn-dark kbola-compose-btn"
                        onClick={onSend}
                        title={t("notificationComposeDrawer.actions.send")}
                        disabled={sending}
                    >
                        <i className="fas fa-paper-plane mr-2" />
                        {sending
                            ? t("notificationComposeDrawer.actions.sending")
                            : t("notificationComposeDrawer.actions.send")}
                    </button>
                </div>
            </div>

            <style>{`
        .kbola-compose-overlay{
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.18);
          z-index: 1200;
        }

        .kbola-compose-drawer{
          position: fixed;
          top: 76px;
          right: 16px;
          bottom: 16px;
          width: min(520px, calc(100vw - 32px));
          max-height: calc(100vh - 92px);
          background: #fff;
          border-radius: 18px;
          border: 1px solid rgba(0,0,0,0.10);
          box-shadow: 0 26px 70px rgba(0,0,0,0.22);
          overflow: hidden;
          z-index: 1201;
          display: flex;
          flex-direction: column;
          animation: kbolaDrawerIn 160ms ease forwards;
        }

        @keyframes kbolaDrawerIn{
          from { transform: translateX(18px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .kbola-compose-head{
          padding: 12px 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          background: linear-gradient(
            135deg,
            ${alphaFromHex(colors.primary, 0.50)},
            ${alphaFromHex(colors.primary, 0.00)}
          );
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }

        .kbola-compose-head-left{
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .kbola-compose-badge{
          width: 34px;
          height: 34px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: ${colors.primary};
          border: 1px solid rgba(0,0,0,0.10);
          color: ${colors.textOnPrimary};
          flex: 0 0 34px;
        }

        .kbola-compose-title{
          font-size: 14px;
          font-weight: 900;
          color: #14151a;
          line-height: 1.1;
        }

        .kbola-compose-subtitle{
          margin-top: 2px;
          font-size: 12.5px;
          font-weight: 700;
          color: rgba(20,21,26,0.55);
        }

        .kbola-compose-close{
          border-radius: 14px !important;
          padding: 10px 12px !important;
          font-weight: 900 !important;
        }

        .kbola-compose-recipientbar{
          padding: 10px 14px;
          border-bottom: 1px solid rgba(0,0,0,0.06);
          background: rgba(0,0,0,0.01);
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .kbola-compose-recipientpill{
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 1px solid rgba(0,0,0,0.10);
          background: #fff;
          border-radius: 999px;
          padding: 7px 10px;
          font-size: 12px;
          font-weight: 900;
          color: rgba(20,21,26,0.84);
        }

        .kbola-compose-recipientlabel{
          color: rgba(20,21,26,0.60);
          font-weight: 900;
        }

        .kbola-compose-recipientvalue{
          color: rgba(20,21,26,0.92);
          font-weight: 950;
        }

        .kbola-compose-body{
          padding: 14px;
          overflow-y: auto;
          overflow-x: hidden;
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
          -webkit-overflow-scrolling: touch;
        }

        .kbola-compose-field{
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .kbola-compose-label{
          font-size: 12px;
          font-weight: 900;
          letter-spacing: 0.02em;
          color: rgba(20,21,26,0.70);
        }

        .kbola-compose-input,
        .kbola-compose-select,
        .kbola-compose-textarea{
          border-radius: 14px !important;
          font-weight: 800;
          border: 1px solid rgba(0,0,0,0.10) !important;
        }

        .kbola-compose-divider{
          height: 1px;
          background: rgba(0,0,0,0.08);
          margin: 4px 0;
        }

        .kbola-compose-teamchips{
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .kbola-compose-chip{
          border: 1px solid rgba(0,0,0,0.10);
          background: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.04);
          color: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.85);
          border-radius: 999px;
          padding: 8px 10px;
          font-weight: 950;
          font-size: 12px;
          cursor: pointer;
          transition: transform 120ms ease, background 120ms ease;
        }

        .kbola-compose-chip:hover{
          background: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.07);
        }

        .kbola-compose-chip:active{
          transform: translateY(1px);
        }

        .kbola-compose-chip.is-active{
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.80);
          color: rgba(20,21,26,0.92);
          border-color: rgba(0,0,0,0.12);
        }

        .kbola-compose-hint{
          margin-top: 6px;
          font-size: 12px;
          font-weight: 800;
          color: rgba(20,21,26,0.55);
        }

        .kbola-compose-footer{
          padding: 12px 14px;
          border-top: 1px solid rgba(0,0,0,0.06);
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          background: #fff;
        }

        .kbola-compose-btn{
          border-radius: 14px !important;
          font-weight: 900 !important;
          padding: 10px 12px !important;
        }

        @media (max-width: 768px){
          .kbola-compose-drawer{
            top: 12px;
            right: 12px;
            left: 12px;
            bottom: 12px;
            width: auto;
          }
        }

        .kbola-compose-head,
        .kbola-compose-recipientbar,
        .kbola-compose-footer{
          flex: 0 0 auto;
        }
      `}</style>
        </>
    );
}